import React, { useEffect } from "react"
import {
    Box,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import { CardGiftcard, Search } from "@material-ui/icons"
import { useSnackbar } from "../../../utils/notificacoes"
import { useBrindesResgatadosFornecedorContext } from "../context/useBrindesResgatadosFornecedorContext"
import { BrindesResgatadosFornecedorActionsTypes } from "../context/brindesResgatadosFornecedorActions"

const useStyles = makeStyles(theme => ({
    container: {
        paddingLeft: "90px",
        paddingRight: "10px",
        paddingTop: "100px",
        paddingBottom: "30px",
        [theme.breakpoints.down("md")]: { paddingLeft: "20px", paddingRight: "20px", paddingTop: "80px" },
        [theme.breakpoints.down("sm")]: { paddingLeft: "15px", paddingRight: "15px", paddingTop: "70px" },
    },
    content: {
        margin: "auto",
        maxWidth: "900px",
        [theme.breakpoints.down("sm")]: { maxWidth: "100%" },
    },
    titleText: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.5rem" },
        [theme.breakpoints.down("xs")]: { fontSize: "1.25rem" },
    },
    subtitle: {
        color: "#7f8c8d",
        marginBottom: theme.spacing(3),
    },
    configCard: {
        borderRadius: 20,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        backgroundColor: "white",
        overflow: "hidden",
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { borderRadius: 15, marginBottom: theme.spacing(2) },
    },
    cardHeader: {
        backgroundColor: "#f8f9fa",
        padding: theme.spacing(2.5),
        borderBottom: "2px solid #e9ecef",
        display: "flex",
        alignItems: "center",
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    cardHeaderTitle: {
        fontWeight: 600,
        color: "#2c3e50",
    },
    cardContent: {
        padding: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    iconHeader: {
        color: "#4caf50",
        marginRight: theme.spacing(1),
    },
    filtrosRow: {
        display: "flex",
        gap: theme.spacing(2),
        alignItems: "flex-end",
        flexWrap: "wrap",
        marginBottom: theme.spacing(3),
    },
    filtroField: {
        flex: 1,
        minWidth: 200,
    },
    btnPesquisar: {
        borderRadius: 25,
        backgroundColor: "var(--cor-botao-salvar)",
        color: "white",
        fontWeight: 600,
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3),
        boxShadow: "0 4px 12px rgba(var(--cor-botao-salvar-rgb),0.3)",
        "&:hover": {
            backgroundColor: "var(--cor-botao-salvar-hover)",
        },
    },
    totalRegistros: {
        color: "#7f8c8d",
        marginTop: theme.spacing(2),
        fontSize: "0.9rem",
    },
    tableHead: {
        backgroundColor: "#f8f9fa",
    },
    tableHeadCell: {
        fontWeight: 600,
        color: "#2c3e50",
    },
    nomeCell: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        maxWidth: 180,
    },
}))

export function BrindesResgatadosLista() {
    const classes = useStyles()
    const { dados, dispatch, pesquisar, erroCarregamento } = useBrindesResgatadosFornecedorContext()
    const { enqueueSnackbar } = useSnackbar()

    const alertStart = (msg: string, tipo: "success" | "error" | "info") => {
        enqueueSnackbar(msg, { variant: tipo })
    }

    useEffect(() => {
        if (erroCarregamento) alertStart(erroCarregamento, "error")
    }, [erroCarregamento])

    const handlePesquisar = (e: React.FormEvent) => {
        e.preventDefault()
        pesquisar()
    }

    return (
        <Box className={classes.container}>
            <Box className={classes.content}>
                <Typography variant="h4" className={classes.titleText} gutterBottom>
                    Brindes Resgatados
                </Typography>
                <Typography variant="body1" className={classes.subtitle}>
                    Nessa tela você verifica todos os brindes resgatados pelos seus clientes.
                </Typography>

                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <CardGiftcard className={classes.iconHeader} />
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Filtrar Brindes
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <form onSubmit={handlePesquisar}>
                            <Box className={classes.filtrosRow}>
                                <TextField
                                    label="Nome do cliente"
                                    variant="outlined"
                                    size="small"
                                    className={classes.filtroField}
                                    value={dados.parteNome}
                                    onChange={e =>
                                        dispatch({
                                            type: BrindesResgatadosFornecedorActionsTypes.ATUALIZAR_FILTRO,
                                            payload: { campo: "parteNome", valor: e.target.value },
                                        })
                                    }
                                />
                                <TextField
                                    label="E-mail do cliente"
                                    variant="outlined"
                                    size="small"
                                    className={classes.filtroField}
                                    value={dados.email}
                                    onChange={e =>
                                        dispatch({
                                            type: BrindesResgatadosFornecedorActionsTypes.ATUALIZAR_FILTRO,
                                            payload: { campo: "email", valor: e.target.value },
                                        })
                                    }
                                />
                                <Button
                                    type="submit"
                                    variant="contained"
                                    className={classes.btnPesquisar}
                                    startIcon={<Search />}
                                >
                                    Pesquisar
                                </Button>
                            </Box>
                        </form>

                        <TableContainer style={{ maxHeight: 480, overflowX: "auto" }}>
                            <Table
                                size="small"
                                stickyHeader
                                style={{ minWidth: 640, width: "100%" }}
                            >
                                <TableHead>
                                    <TableRow>
                                        <TableCell
                                            className={classes.tableHeadCell}
                                            style={{ minWidth: 160 }}
                                        >
                                            Cliente
                                        </TableCell>
                                        <TableCell
                                            className={classes.tableHeadCell}
                                            style={{ minWidth: 160 }}
                                        >
                                            Título
                                        </TableCell>
                                        <TableCell
                                            className={classes.tableHeadCell}
                                            style={{ minWidth: 200 }}
                                        >
                                            Descrição
                                        </TableCell>
                                        <TableCell
                                            className={classes.tableHeadCell}
                                            style={{ minWidth: 120 }}
                                        >
                                            Data do Resgate
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {dados.brindes.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={4} align="center">
                                                <Typography
                                                    variant="body2"
                                                    style={{ color: "#7f8c8d", padding: "16px 0" }}
                                                >
                                                    Nenhum brinde resgatado encontrado.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        dados.brindes.map((brinde, index) => (
                                            <TableRow key={index} hover>
                                                <TableCell
                                                    className={classes.nomeCell}
                                                    title={brinde.cliente}
                                                >
                                                    {brinde.cliente}
                                                </TableCell>
                                                <TableCell
                                                    className={classes.nomeCell}
                                                    title={brinde.titulo}
                                                >
                                                    {brinde.titulo}
                                                </TableCell>
                                                <TableCell>{brinde.descricao}</TableCell>
                                                <TableCell>{brinde.dataResgate}</TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <Typography className={classes.totalRegistros}>
                            Exibindo {dados.brindes.length} registro(s).
                        </Typography>
                    </Box>
                </Paper>
            </Box>
        </Box>
    )
}
